import { useContext, useEffect, useState } from "react";
import { Sidebar, Menu, useProSidebar } from "react-pro-sidebar";
import { FaBars } from "react-icons/fa";
import { NavLink, useNavigate } from "react-router-dom";
import { Tooltip } from "@mui/material";
import { useDispatch } from "react-redux";
import { logout } from "../../redux/features/auth/authSlice";
import { GlobalStateContext } from "../context/GlobalStateContext";
import { useTinodeConnection } from "../../hooks/useTinodeConnection";
import {
  TbMessage,
  TbListSearch,
  TbUserCircle,
  TbLogout,
  TbHome,
} from "react-icons/tb";
import { sendDoctorWebPresenceOffline } from "../../services/telemedicineApi";
import { getDoctorWebPresenceSessionId } from "../../hooks/useDoctorWebPresenceHeartbeat";
import { BiFullscreen, BiExitFullscreen } from "react-icons/bi";
import { readSafeStorage } from "../../utils/localStorageHelper";

const activeClass =
  "bg-[#E5EBF1] w-full block text-primary border-l-8 border-primary flex items-center";

const SidebarComponent = () => {
  const { isLogin, setIsLogin } = useContext(GlobalStateContext);
  const { toggleSidebar, broken } = useProSidebar();
  const role = localStorage.getItem("role");
  const isDoctorActive = localStorage.getItem("is_doctor_active") === "true";
  const outletLogo = readSafeStorage("outlet_logo", "");

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const currentUser = {
    fullname: localStorage.getItem("fullname") || "User",
    email: localStorage.getItem("email") || "",
    id: localStorage.getItem("user_id") || "",
    userid_tinode: localStorage.getItem("user_id") || "",
  };

  const { tinodeRef, clearAuthToken } = useTinodeConnection(currentUser);

  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showFullscreenButton, setShowFullscreenButton] = useState(true);

  useEffect(() => {
    const checkIOS = () => {
      return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    };

    const iosDevice = checkIOS();

    setShowFullscreenButton(!iosDevice);
  }, []);

  useEffect(() => {
    if (!showFullscreenButton) return;

    const handleFullscreenChange = () => {
      setIsFullscreen(
        !!document.fullscreenElement ||
          !!document.webkitFullscreenElement ||
          !!document.mozFullScreenElement ||
          !!document.msFullscreenElement,
      );
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    document.addEventListener("mozfullscreenchange", handleFullscreenChange);
    document.addEventListener("MSFullscreenChange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener(
        "webkitfullscreenchange",
        handleFullscreenChange,
      );
      document.removeEventListener(
        "mozfullscreenchange",
        handleFullscreenChange,
      );
      document.removeEventListener(
        "MSFullscreenChange",
        handleFullscreenChange,
      );
    };
  }, [showFullscreenButton]);

  const toggleFullScreen = () => {
    if (!showFullscreenButton) return;

    const doc = document.documentElement;

    if (
      !document.fullscreenElement &&
      !document.webkitFullscreenElement &&
      !document.mozFullScreenElement &&
      !document.msFullscreenElement
    ) {
      if (doc.requestFullscreen) {
        doc.requestFullscreen();
      } else if (doc.webkitRequestFullscreen) {
        doc.webkitRequestFullscreen();
      } else if (doc.mozRequestFullScreen) {
        doc.mozRequestFullScreen();
      } else if (doc.msRequestFullscreen) {
        doc.msRequestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
    }
  };

  const handleLogout = async () => {
    if (role === "2") {
      const sessionId = getDoctorWebPresenceSessionId();
      if (sessionId) {
        try {
          await sendDoctorWebPresenceOffline({ session_id: sessionId });
        } catch (_) {}
      }
    }

    dispatch(logout());
    setIsLogin(false);
    if (tinodeRef.current) {
      clearAuthToken();
      tinodeRef.current.disconnect();
    }
    sessionStorage.removeItem("tinode_password");
    if (role === "2") {
      navigate("/login");
    } else {
      navigate("/purchase");
    }
  };

  return (
    <div className="flex h-[100%]">
      <Sidebar
        breakPoint="lg"
        backgroundColor="white"
        className="shadow-md"
        width="96px"
      >
        <div className="flex flex-col w-full h-full justify-between">
          <div>
            <div className="w-full min-h-32 flex items-center justify-center object-cover">
              <img src={outletLogo} alt="Outlet Logo" className="w-full px-2" />
            </div>

            {isLogin ? (
              <Menu>
                <NavLink
                  to="/doctor-dashboard"
                  className={({ isActive }) =>
                    isActive ? activeClass : "text-slate-700"
                  }
                >
                  <div className="w-full flex flex-col items-center justify-center gap-1 py-4 hover:bg-indigo-100 transform duration-500">
                    <TbHome size={28} />
                    <p className="text-md font-medium">Dashboard</p>
                  </div>
                </NavLink>
                {isDoctorActive && (
                  <NavLink
                    to="/consultations"
                    className={({ isActive }) =>
                      isActive ? activeClass : "text-slate-700"
                    }
                  >
                    <div className="w-full flex flex-col items-center justify-center gap-1 py-4 hover:bg-indigo-100 transform duration-500">
                      <TbMessage size={28} />
                      <p className="text-md font-medium">Pesan</p>
                    </div>
                  </NavLink>
                )}
                {role === "2" && (
                  <NavLink
                    to="/history"
                    className={({ isActive }) =>
                      isActive ? activeClass : "text-slate-700"
                    }
                  >
                    <div className="w-full flex flex-col items-center justify-center gap-1 py-4 hover:bg-indigo-100 transform duration-500">
                      <TbListSearch size={28} />
                      <p className="text-md font-medium">Histori</p>
                    </div>
                  </NavLink>
                )}
                <NavLink
                  to="/doctor-profile"
                  className={({ isActive }) =>
                    isActive ? activeClass : "text-slate-700"
                  }
                >
                  <div className="w-full flex flex-col items-center justify-center gap-1 py-4 hover:bg-indigo-100 transform duration-500">
                    <TbUserCircle size={28} />
                    <p className="text-md font-medium">Profil</p>
                  </div>
                </NavLink>
                <button
                  onClick={toggleFullScreen}
                  className="w-full text-slate-700 flex items-center"
                >
                  <div className="w-full flex flex-col items-center justify-center gap-1 py-4 hover:bg-indigo-100 transform duration-500">
                    {isFullscreen ? (
                      <>
                        <BiExitFullscreen size={28} />
                        <p className="text-md font-medium">Minimize</p>
                      </>
                    ) : (
                      <>
                        <BiFullscreen size={28} />
                        <p className="text-md font-medium">Maximize</p>
                      </>
                    )}
                  </div>
                </button>
              </Menu>
            ) : null}
          </div>

          <div>
            {isLogin ? (
              <div className="flex items-center">
                <Tooltip title="Logout">
                  <div
                    onClick={handleLogout}
                    className="w-full flex flex-col items-center justify-center gap-1 py-4 hover:bg-red-100 transform duration-500 cursor-pointer text-slate-700"
                  >
                    <TbLogout size={28} />
                    <p className="text-md font-medium">Logout</p>
                  </div>
                </Tooltip>
              </div>
            ) : null}
          </div>
        </div>
      </Sidebar>
      <main>
        <div style={{ display: "flex", padding: 10 }}>
          {broken ? (
            <button
              className="sb-button fixed z-30 top-[15px] right-5"
              onClick={() => toggleSidebar()}
            >
              <FaBars />
            </button>
          ) : null}
        </div>
      </main>
    </div>
  );
};

export default SidebarComponent;
